/**
 * BulkImportModal — 4-step wizard for CSV/JSON bulk import.
 *
 * Steps: Upload → Column Map → Preview → Import
 * Glassmorphism styled to match existing modals.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, FileText, AlertTriangle, CheckCircle2, XCircle, Copy } from 'lucide-react';
import type { AdjacencyNode, CreateNodeInput } from '../../types/adjacency';
import { parseCSV } from '../../utils/csv-parser';
import type { ParsedCSV } from '../../utils/csv-parser';
import {
  autoDetectMappings,
  validateAndTransform,
  validateJsonImport,
  IMPORTABLE_FIELDS,
} from '../../utils/import-validator';
import type { ColumnMapping, ImportValidationResult, NodeField } from '../../utils/import-validator';

// ── Types ─────────────────────────────────────────────────────────────

type Step = 'upload' | 'map' | 'preview' | 'done';

interface BulkImportModalProps {
  existingNodes: AdjacencyNode[];
  onImport: (inputs: CreateNodeInput[]) => Promise<AdjacencyNode[]>;
  onClose: () => void;
}

// ── Component ─────────────────────────────────────────────────────────

export function BulkImportModal({ existingNodes, onImport, onClose }: BulkImportModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [fileType, setFileType] = useState<'csv' | 'json'>('csv');
  const [fileName, setFileName] = useState('');
  const [parsedCSV, setParsedCSV] = useState<ParsedCSV | null>(null);
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [validation, setValidation] = useState<ImportValidationResult | null>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importCount, setImportCount] = useState(0);
  const [importError, setImportError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Escape to close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // ── File processing ─────────────────────────────────────────────────

  const processFile = useCallback((file: File) => {
    setFileName(file.name);
    const isJson = file.name.toLowerCase().endsWith('.json');
    setFileType(isJson ? 'json' : 'csv');

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (isJson) {
        try {
          const data = JSON.parse(text);
          const result = validateJsonImport(data, existingNodes);
          setValidation(result);
          setStep('preview');
        } catch {
          setValidation({
            rows: [{ index: 0, input: null, errors: ['Invalid JSON'], warnings: [], isDuplicate: false }],
            validCount: 0,
            errorCount: 1,
            duplicateCount: 0,
          });
          setStep('preview');
        }
      } else {
        const parsed = parseCSV(text);
        setParsedCSV(parsed);
        const detected = autoDetectMappings(parsed.headers);
        setMappings(detected);
        setStep('map');
      }
    };
    reader.readAsText(file);
  }, [existingNodes]);

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  // ── Column mapping ──────────────────────────────────────────────────

  const updateMapping = useCallback((index: number, field: NodeField | null) => {
    setMappings((prev) => prev.map((m, i) => i === index ? { ...m, nodeField: field } : m));
  }, []);

  const hasNameMapped = mappings.some((m) => m.nodeField === 'name');

  const handleApplyMappings = useCallback(() => {
    if (!parsedCSV) return;
    const result = validateAndTransform(parsedCSV.rows, mappings, existingNodes);
    setValidation(result);
    setStep('preview');
  }, [parsedCSV, mappings, existingNodes]);

  // ── Import ──────────────────────────────────────────────────────────

  const handleImport = useCallback(async () => {
    if (!validation) return;
    setImporting(true);
    setImportError(null);

    const toImport = validation.rows
      .filter((r) => r.input !== null && r.errors.length === 0)
      .filter((r) => !skipDuplicates || !r.isDuplicate)
      .map((r) => r.input!);

    try {
      const created = await onImport(toImport);
      setImportCount(created.length);
      setStep('done');
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  }, [validation, skipDuplicates, onImport]);

  const importableCount = validation
    ? validation.rows.filter(
        (r) => r.input !== null && r.errors.length === 0 && (!skipDuplicates || !r.isDuplicate),
      ).length
    : 0;

  // ── Step titles ─────────────────────────────────────────────────────

  const stepTitles: Record<Step, string> = {
    upload: 'Bulk Import',
    map: 'Map Columns',
    preview: 'Preview Import',
    done: 'Import Complete',
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[1100]" style={{ background: 'var(--modal-backdrop)' }} onClick={onClose} />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        className="fixed top-1/2 left-1/2 z-[1200] w-[560px] max-h-[85vh] flex flex-col rounded-2xl"
        style={{
          transform: 'translate(-50%, -50%)',
          background: 'var(--modal-bg)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(59, 130, 246, 0.25)',
          boxShadow: 'var(--modal-shadow), 0 0 40px rgba(59,130,246,0.1)',
        }}
      >
        {/* ── Header ───────────────────────────────────────── */}
        <div
          className="flex items-center justify-between px-5 py-3 shrink-0"
          style={{ borderBottom: '1px solid var(--glass-border)' }}
        >
          <div className="flex items-center gap-2">
            <Upload size={16} style={{ color: 'var(--color-blue-400)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              {stepTitles[step]}
            </span>
            {fileName && step !== 'upload' && (
              <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ color: 'var(--text-muted)', background: 'rgba(255,255,255,0.06)' }}>
                {fileName}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors"
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}
          >
            <X size={16} />
          </button>
        </div>

        {/* ── Body ─────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-5">
          {step === 'upload' && (
            <UploadStep
              dragOver={dragOver}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleFileDrop}
              onClickBrowse={() => fileInputRef.current?.click()}
              fileInputRef={fileInputRef}
              onFileSelect={handleFileSelect}
            />
          )}

          {step === 'map' && parsedCSV && (
            <MapColumnsStep
              headers={parsedCSV.headers}
              mappings={mappings}
              sampleRow={parsedCSV.rows[0]}
              onUpdateMapping={updateMapping}
              hasNameMapped={hasNameMapped}
              rowCount={parsedCSV.rowCount}
              onApply={handleApplyMappings}
              onBack={() => setStep('upload')}
            />
          )}

          {step === 'preview' && validation && (
            <PreviewStep
              validation={validation}
              skipDuplicates={skipDuplicates}
              onToggleSkipDuplicates={() => setSkipDuplicates((v) => !v)}
              importableCount={importableCount}
              importing={importing}
              importError={importError}
              onImport={handleImport}
              onBack={() => fileType === 'csv' ? setStep('map') : setStep('upload')}
            />
          )}

          {step === 'done' && (
            <DoneStep importCount={importCount} onClose={onClose} />
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

// ── Step 1: Upload ────────────────────────────────────────────────────

function UploadStep({
  dragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onClickBrowse,
  fileInputRef,
  onFileSelect,
}: {
  dragOver: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  onClickBrowse: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="flex flex-col items-center gap-4">
      <div
        className="w-full rounded-xl p-8 flex flex-col items-center gap-3 cursor-pointer transition-colors"
        style={{
          border: `2px dashed ${dragOver ? 'rgba(59,130,246,0.6)' : 'rgba(255,255,255,0.12)'}`,
          background: dragOver ? 'rgba(59,130,246,0.08)' : 'rgba(255,255,255,0.03)',
        }}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={onClickBrowse}
      >
        <FileText size={32} style={{ color: 'var(--color-blue-400)', opacity: 0.7 }} />
        <div className="text-center">
          <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
            Drop a CSV or JSON file here
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
            or click to browse
          </p>
        </div>
      </div>
      <input
        ref={fileInputRef as React.LegacyRef<HTMLInputElement>}
        type="file"
        accept=".csv,.json"
        className="hidden"
        onChange={onFileSelect}
      />
      <div className="text-[11px] leading-relaxed" style={{ color: 'var(--text-muted)' }}>
        <p><strong>CSV:</strong> First row must be headers. Columns are auto-mapped in the next step.</p>
        <p><strong>JSON:</strong> Array of objects with <code style={{ color: 'var(--color-blue-300)' }}>name</code> field required. Imports directly.</p>
      </div>
    </div>
  );
}

// ── Step 2: Map Columns ───────────────────────────────────────────────

function MapColumnsStep({
  headers,
  mappings,
  sampleRow,
  onUpdateMapping,
  hasNameMapped,
  rowCount,
  onApply,
  onBack,
}: {
  headers: string[];
  mappings: ColumnMapping[];
  sampleRow?: string[];
  onUpdateMapping: (index: number, field: NodeField | null) => void;
  hasNameMapped: boolean;
  rowCount: number;
  onApply: () => void;
  onBack: () => void;
}) {
  // Track which fields are already assigned
  const usedFields = new Set(mappings.map((m) => m.nodeField).filter(Boolean));

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        {rowCount} data row{rowCount !== 1 ? 's' : ''} detected. Map CSV columns to node fields.
        <span style={{ color: 'var(--color-blue-300)' }}> Name</span> is required.
      </p>

      <div className="flex flex-col gap-1.5">
        {headers.map((header, i) => (
          <div
            key={i}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
            style={{ background: 'rgba(255,255,255,0.04)' }}
          >
            <div className="flex-1 min-w-0">
              <span className="text-xs font-medium truncate block" style={{ color: 'var(--text-primary)' }}>
                {header}
              </span>
              {sampleRow?.[i] && (
                <span className="text-[10px] truncate block" style={{ color: 'var(--text-muted)' }}>
                  e.g. "{sampleRow[i]}"
                </span>
              )}
            </div>
            <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>→</span>
            <select
              value={mappings[i]?.nodeField ?? ''}
              onChange={(e) => onUpdateMapping(i, (e.target.value || null) as NodeField | null)}
              className="w-40 text-xs rounded-md px-2 py-1 bg-transparent outline-none"
              style={{
                color: mappings[i]?.nodeField ? 'var(--text-primary)' : 'var(--text-muted)',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <option value="">— skip —</option>
              {IMPORTABLE_FIELDS.map((f) => (
                <option
                  key={f.value}
                  value={f.value}
                  disabled={usedFields.has(f.value) && mappings[i]?.nodeField !== f.value}
                >
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {/* Footer buttons */}
      <div className="flex items-center justify-between pt-2" style={{ borderTop: '1px solid var(--glass-border)' }}>
        <button
          onClick={onBack}
          className="text-xs px-3 py-1.5 rounded-lg transition-colors"
          style={{ color: 'var(--text-muted)', background: 'rgba(255,255,255,0.06)' }}
        >
          Back
        </button>
        <button
          onClick={onApply}
          disabled={!hasNameMapped}
          className="text-xs px-4 py-1.5 rounded-lg font-medium transition-colors"
          style={{
            background: hasNameMapped ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.06)',
            color: hasNameMapped ? '#93c5fd' : 'var(--text-muted)',
            opacity: hasNameMapped ? 1 : 0.5,
          }}
        >
          Preview →
        </button>
      </div>
    </div>
  );
}

// ── Step 3: Preview ───────────────────────────────────────────────────

function PreviewStep({
  validation,
  skipDuplicates,
  onToggleSkipDuplicates,
  importableCount,
  importing,
  importError,
  onImport,
  onBack,
}: {
  validation: ImportValidationResult;
  skipDuplicates: boolean;
  onToggleSkipDuplicates: () => void;
  importableCount: number;
  importing: boolean;
  importError: string | null;
  onImport: () => void;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      {/* Summary bar */}
      <div className="flex items-center gap-3 text-xs">
        <span style={{ color: '#86efac' }}>
          <CheckCircle2 size={12} className="inline mr-1" />
          {validation.validCount} valid
        </span>
        {validation.errorCount > 0 && (
          <span style={{ color: '#fca5a5' }}>
            <XCircle size={12} className="inline mr-1" />
            {validation.errorCount} error{validation.errorCount !== 1 ? 's' : ''}
          </span>
        )}
        {validation.duplicateCount > 0 && (
          <span style={{ color: '#fde68a' }}>
            <Copy size={12} className="inline mr-1" />
            {validation.duplicateCount} duplicate{validation.duplicateCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Skip duplicates toggle */}
      {validation.duplicateCount > 0 && (
        <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
          <input
            type="checkbox"
            checked={skipDuplicates}
            onChange={onToggleSkipDuplicates}
            className="accent-blue-500"
          />
          Skip duplicates
        </label>
      )}

      {/* Scrollable row table */}
      <div
        className="max-h-[300px] overflow-y-auto rounded-lg"
        style={{ border: '1px solid rgba(255,255,255,0.06)' }}
      >
        <table className="w-full text-xs">
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)', position: 'sticky', top: 0, zIndex: 1 }}>
              <th className="text-left px-3 py-2 font-medium" style={{ color: 'var(--text-muted)', width: 28 }}>#</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: 'var(--text-muted)' }}>Status</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: 'var(--text-muted)' }}>Name</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: 'var(--text-muted)' }}>Axis</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: 'var(--text-muted)' }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {validation.rows.map((row) => {
              const isSkipped = skipDuplicates && row.isDuplicate;
              return (
                <tr
                  key={row.index}
                  style={{
                    borderTop: '1px solid rgba(255,255,255,0.04)',
                    opacity: isSkipped || row.errors.length > 0 ? 0.5 : 1,
                  }}
                >
                  <td className="px-3 py-1.5" style={{ color: 'var(--text-muted)' }}>{row.index + 1}</td>
                  <td className="px-3 py-1.5">
                    {row.errors.length > 0 ? (
                      <XCircle size={14} style={{ color: '#fca5a5' }} />
                    ) : row.isDuplicate ? (
                      <AlertTriangle size={14} style={{ color: '#fde68a' }} />
                    ) : (
                      <CheckCircle2 size={14} style={{ color: '#86efac' }} />
                    )}
                  </td>
                  <td className="px-3 py-1.5" style={{ color: 'var(--text-primary)' }}>
                    {row.input?.name || '—'}
                  </td>
                  <td className="px-3 py-1.5" style={{ color: 'var(--text-secondary)' }}>
                    {row.input?.adjacencyAxis || '—'}
                  </td>
                  <td className="px-3 py-1.5" style={{ color: 'var(--text-muted)' }}>
                    {row.errors.length > 0
                      ? row.errors.join('; ')
                      : row.isDuplicate
                        ? `Duplicate of "${row.duplicateOf}"`
                        : row.warnings.length > 0
                          ? row.warnings[0]
                          : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {importError && (
        <p className="text-xs px-3 py-2 rounded-lg" style={{ color: '#fca5a5', background: 'rgba(239,68,68,0.1)' }}>
          {importError}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-2" style={{ borderTop: '1px solid var(--glass-border)' }}>
        <button
          onClick={onBack}
          className="text-xs px-3 py-1.5 rounded-lg transition-colors"
          style={{ color: 'var(--text-muted)', background: 'rgba(255,255,255,0.06)' }}
        >
          Back
        </button>
        <button
          onClick={onImport}
          disabled={importableCount === 0 || importing}
          className="text-xs px-4 py-1.5 rounded-lg font-medium transition-colors"
          style={{
            background: importableCount > 0 && !importing ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.06)',
            color: importableCount > 0 && !importing ? '#93c5fd' : 'var(--text-muted)',
            opacity: importableCount > 0 && !importing ? 1 : 0.5,
          }}
        >
          {importing ? 'Importing...' : `Import ${importableCount} node${importableCount !== 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );
}

// ── Step 4: Done ──────────────────────────────────────────────────────

function DoneStep({ importCount, onClose }: { importCount: number; onClose: () => void }) {
  return (
    <div className="flex flex-col items-center gap-4 py-6">
      <CheckCircle2 size={40} style={{ color: '#86efac' }} />
      <div className="text-center">
        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          {importCount} node{importCount !== 1 ? 's' : ''} imported
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
          Use Ctrl+Z to undo the entire batch.
        </p>
      </div>
      <button
        onClick={onClose}
        className="text-xs px-5 py-2 rounded-lg font-medium transition-colors"
        style={{ background: 'rgba(59,130,246,0.3)', color: '#93c5fd' }}
      >
        Done
      </button>
    </div>
  );
}
